/**
 * Customer Account React hooks — TanStack Query wrappers over api.ts.
 *
 * These are the ONLY public callers of `./api.ts`. Components import from
 * here, not directly from api.ts (per spec §4.1 layered architecture).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerAccountValues } from '@/schemas/customer-account';
import {
  createCompany,
  deleteCompany,
  deleteCompanyDocument,
  deleteCustomerDraft,
  deleteJurisdiction,
  getCompany,
  getCustomerDraft,
  listCompanies,
  listCompanyDocuments,
  submitCompany,
  submitCustomerAccount,
  updateCompany,
  uploadCompanyDocument,
  upsertCustomerDraft,
  upsertJurisdiction,
  type CompanyDto,
  type DraftResponse,
} from './api';

const QUERY_KEY = ['customer-account', 'draft'] as const;

/**
 * Fetch the user's existing draft on mount.
 *
 * Returns:
 *   - `data: undefined`  while loading
 *   - `data: null`       if the user has no draft (backend 204)
 *   - `data: DraftResponse` if a draft (or amend-mode submitted record) exists
 *
 * Stale-time is short (15s) so navigating back to the wizard after a quick
 * external change refreshes without a hard reload.
 */
export function useCustomerDraft() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: getCustomerDraft,
    staleTime: 15 * 1000,
    retry: 1,
    // The customer row is customer-scoped (shared across teammates).
    // Refetch on tab focus so a teammate's amendment or status
    // transition shows up without a hard refresh.
    refetchOnWindowFocus: true,
  });
}

/**
 * PUT (create / overwrite / amend) the draft.
 *
 * On success, optimistically updates the cached draft so navigating away
 * and back doesn't refetch immediately.
 */
export function useUpsertCustomerDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertCustomerDraft,
    onSuccess: (fresh) => {
      queryClient.setQueryData<DraftResponse | null>(QUERY_KEY, fresh);
    },
  });
}

/**
 * DELETE the draft (e.g. when the user clicks Cancel + Confirm).
 */
export function useDeleteCustomerDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomerDraft,
    onSuccess: () => {
      queryClient.setQueryData<DraftResponse | null>(QUERY_KEY, null);
    },
  });
}

/**
 * POST /submit — finalize the draft. After success, the draft cache holds
 * a record with `status: 'submitted'` so the wizard switches to amend mode.
 */
export function useSubmitCustomerAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitCustomerAccount,
    onSuccess: (submitted) => {
      // Submitted response is structurally compatible with DraftResponse for
      // our cache (data + status + created_at + updated_at). Cast accordingly.
      queryClient.setQueryData<DraftResponse | null>(QUERY_KEY, {
        data: submitted.data,
        status: submitted.status,
        created_at: submitted.created_at,
        updated_at: submitted.updated_at,
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Companies (multi-company account)
// ---------------------------------------------------------------------------

const COMPANIES_KEY = ['companies'] as const;

/** List every company in the account (drives the Companies table + parent picker). */
export function useCompanies() {
  return useQuery({
    queryKey: COMPANIES_KEY,
    queryFn: listCompanies,
    staleTime: 15 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
}

/** Fetch one company + its jurisdictions (drives the wizard in edit mode). */
export function useCompany(id: number | undefined) {
  return useQuery({
    queryKey: ['companies', id],
    queryFn: () => getCompany(id as number),
    enabled: id != null,
    staleTime: 15 * 1000,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      base: Partial<CustomerAccountValues>;
      parentCompanyId?: number | null;
    }) => createCompany(vars.base, vars.parentCompanyId ?? null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      base: Partial<CustomerAccountValues>;
      parentCompanyId?: number | null;
    }) => updateCompany(vars.id, vars.base, vars.parentCompanyId ?? null),
    onSuccess: (fresh: CompanyDto) => {
      queryClient.setQueryData(['companies', fresh.id], fresh);
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

export function useSubmitCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => submitCompany(id),
    onSuccess: (fresh: CompanyDto) => {
      queryClient.setQueryData(['companies', fresh.id], fresh);
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

export function useUpsertJurisdiction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      companyId: number;
      marketCode: string;
      data: Record<string, unknown>;
    }) => upsertJurisdiction(vars.companyId, vars.marketCode, vars.data),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['companies', vars.companyId] });
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

export function useDeleteJurisdiction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { companyId: number; marketCode: string }) =>
      deleteJurisdiction(vars.companyId, vars.marketCode),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['companies', vars.companyId] });
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

// ── Supporting documents (Section 11) ───────────────────────────────────────

const companyDocsKey = (id: number) => ['company-documents', id] as const;

/** A company's uploaded supporting documents (+ extraction summary). */
export function useCompanyDocuments(companyId: number | undefined) {
  return useQuery({
    queryKey: companyId == null ? ['company-documents', 'none'] : companyDocsKey(companyId),
    queryFn: () => listCompanyDocuments(companyId as number),
    enabled: companyId != null,
    staleTime: 15 * 1000,
  });
}

export function useUploadCompanyDocument(companyId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { docType: string; file: File }) =>
      uploadCompanyDocument(companyId as number, vars.docType, vars.file),
    onSuccess: () => {
      if (companyId != null)
        queryClient.invalidateQueries({ queryKey: companyDocsKey(companyId) });
    },
  });
}

export function useDeleteCompanyDocument(companyId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: number) =>
      deleteCompanyDocument(companyId as number, attachmentId),
    onSuccess: () => {
      if (companyId != null)
        queryClient.invalidateQueries({ queryKey: companyDocsKey(companyId) });
    },
  });
}
